import clsx from 'clsx'
import type React from 'react'
import { useEffect, useRef } from 'react'

export interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: React.ReactNode
  subtitle?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  className?: string
  variant?: 'default' | 'bottom-sheet'
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  className,
  variant = 'default',
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (isOpen) {
      dialog.show()
    } else {
      dialog.close()
    }
  }, [isOpen])

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Mark siblings as inert when open to trap focus inside the dialog
  useEffect(() => {
    if (!isOpen) return
    const dialog = dialogRef.current
    if (!dialog) return

    const parent = dialog.parentElement
    if (!parent) return

    const siblings = Array.from(parent.children).filter(
      (el) =>
        el !== dialog &&
        !(el instanceof HTMLButtonElement && el.getAttribute('aria-label') === 'Close modal')
    )
    for (const el of siblings) {
      el.setAttribute('inert', '')
    }
    return () => {
      for (const el of siblings) {
        el.removeAttribute('inert')
      }
    }
  }, [isOpen])

  return (
    <>
      {/* Manual backdrop — showModal()'s ::backdrop uses the top layer which hides toasts */}
      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/50 w-full h-full cursor-default appearance-none border-none p-0"
          onClick={onClose}
          tabIndex={-1}
          aria-label="Close modal"
        />
      )}
      <dialog
        ref={dialogRef}
        aria-modal="true"
        className={clsx(
          variant === 'bottom-sheet'
            ? 'p-0 w-full max-w-none m-0 md:max-w-2xl md:m-auto md:rounded-lg md:shadow'
            : 'p-0 rounded-lg shadow max-w-2xl w-full m-auto',
          className
        )}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
        }}
      >
        <div
          className={
            variant === 'bottom-sheet'
              ? 'bg-white shadow rounded-t-lg fixed bottom-0 inset-x-0 flex flex-col max-h-[85dvh] md:static md:rounded-lg md:max-h-[calc(100dvh-4rem)]'
              : 'bg-white rounded-lg shadow flex flex-col max-h-[calc(100dvh-4rem)]'
          }
        >
          <div className="shrink-0 flex items-end justify-between p-4 pb-0 rounded-t">
            <h3 className="font-ginto-nord-md text-xl font-semibold text-charcoal">{title}</h3>
            <button
              onClick={onClose}
              type="button"
              className="text-charcoal bg-transparent rounded-lg text-sm w-8 h-8 ml-auto inline-flex justify-center items-center hover:bg-charcoal-25/10 transition-colors"
            >
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          {subtitle && <h4 className="shrink-0 px-4 text-charcoal-50">{subtitle}</h4>}
          <div className={clsx('p-4 space-y-6 overflow-y-auto min-h-0 flex-1', footer && 'pb-0')}>
            {children}
          </div>
          {footer && <div className="shrink-0 px-4 pb-4">{footer}</div>}
        </div>
      </dialog>
    </>
  )
}
